import { acceptHMRUpdate, defineStore } from 'pinia'
import api from '@api'
import type {
  ContractsTendersListParams,
  ContractsTendersPriceOffersParams,
  TenderFirstStep,
  TenderFourthStepCarriers,
  TenderSecondStepRoutes,
  TenderThirdStepPricingCategories,
  TenderUpdate,
} from '@apiTypes'

export const useTendersStore = defineStore('tenders', {
  actions: {
    fetchTenders(query: ContractsTendersListParams) {
      return api.contracts
        .contractsTendersList(query)
    },

    saveFirstStep(data: TenderFirstStep) {
      return api.contracts
        .contractsTendersSaveFirstStepCreate(data)
        .then((response) => response.data)
    },

    saveSecondStep(id: number, data: TenderSecondStepRoutes) {
      return api.contracts
        .contractsTendersSaveSecondStep({ id }, data)
        .then((response) => response.data)
    },

    saveThirdStep(id: number, data: TenderThirdStepPricingCategories) {
      return api.contracts
        .contractsTendersSaveThirdStep({ id }, data)
        .then((response) => response.data)
    },

    saveFourthStep(id: number, data: TenderFourthStepCarriers) {
      return api.contracts
        .contractsTendersSaveFourthStep({ id }, data)
        .then((response) => response.data)
    },

    fetchOverview(id: number) {
      return api.contracts
        .contractsTendersOverview({ id })
        .then((response) => response.data)
    },

    fetchFilters(id: number) {
      return api.contracts
        .contractsTendersFiltersRetrieve({ id })
        .then((response) => response.data)
    },

    fetchTender(id: number) {
      return api.contracts
        .contractsTendersRetrieve({ id })
    },

    fetchCounts(id: number) {
      return api.contracts.contractsTendersCounts({ id })
    },

    fetchPriceOffers(id: number, query: Omit<ContractsTendersPriceOffersParams, 'id'>) {
      return api.contracts
        .contractsTendersPriceOffers({ id, ...query })
    },

    publishTender(id: number) {
      return api.contracts
        .contractsTendersPublish({ id })
        .then((response) => response.data)
    },

    validatePublish(id: number): Promise<void> {
      return api.contracts
        .contractsTendersValidatePublish({ id })
        .then(() => undefined)
    },

    validateFinish(id: number): Promise<void> {
      return api.contracts
        .contractsTendersValidateFinish({ id })
        .then(() => undefined)
    },

    proposeContract(id: number, offerIds: number[]): Promise<void> {
      return api.contracts
        .contractsTendersProposeContract({ id }, { offerIds })
        .then(() => undefined)
    },

    exportOffersToXlsx(id: number) {
      return api.contracts
        .contractsTendersOfferExport({ id })
    },

    updateTender(id: number, data: TenderUpdate) {
      return api.contracts
        .contractsTendersUpdate({ id }, data)
        .then((response) => response.data)
    },

    cancelTender(id: number) {
      return api.contracts.contractsTendersCancel({ id })
    },

    finishTender(id: number) {
      return api.contracts.contractsTendersFinish({ id })
    },

    deleteTender(id: number): Promise<void> {
      return api.contracts
        .contractsTendersDestroy({ id })
        .then(() => undefined)
    },
  },
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useTendersStore, import.meta.hot))
